import { useEffect } from 'react';
import { useBoolean } from 'ahooks';
import { Button, Drawer, Form, Input, InputNumber, message, Select, Space } from 'antd';
import { FormattedMessage, useIntl } from 'react-intl';

import {
    capitalOperationRecordsCreate,
    capitalOperationRecordsDetail,
    type CapitalOperationRecord,
    type CapitalOperationRecordCreatePayload,
    type CapitalOperationType,
} from '@/services/capital-operation-records';

import type { DrawerProps, FormProps } from 'antd';

const TYPE_OPTIONS: { value: CapitalOperationType; label: string }[] = [
    { value: 32, label: '空投' },
    { value: 33, label: '其他加线' },
    { value: 34, label: '其他扣线' },
];

const TOKEN_OPTIONS = [{ value: 'USDT', label: 'USDT' }];

export type DrawerFormState = {
    open: boolean;
    mode: 'create' | 'detail';
    /** 详情时传入 id，创建时无 */
    id?: string;
    /** 详情时预填数据（若已有），避免闪烁 */
    initialData?: CapitalOperationRecord | null;
};

type Props = {
    state: DrawerFormState;
    onClose: (reload?: boolean) => void;
};

type FormValues = {
    user_id: string;
    type: CapitalOperationType;
    token: string;
    amount: string | number;
    reason: string;
};

const ComponentDrawerForm: React.FC<Props> = ({ state, onClose }) => {
    const [form] = Form.useForm<FormValues>();
    const intl = useIntl();
    const [submitting, { setTrue: setSubmittingTrue, setFalse: setSubmittingFalse }] = useBoolean(false);

    const isDetail = state.mode === 'detail';

    const drawerProps: DrawerProps = {
        destroyOnClose: true,
        maskClosable: false,
        open: state.open,
        title: state.mode === 'create' ? '资金操作' : '资金操作',
        footer:
            state.mode === 'detail'
                ? undefined
                : (
                      <div style={{ textAlign: 'right' }}>
                          <Space>
                              <Button onClick={() => onClose()}>
                                  <FormattedMessage id="b.cancel" />
                              </Button>
                              <Button
                                  type="primary"
                                  loading={submitting}
                                  onClick={() => {
                                      form.validateFields().then(async (values: FormValues) => {
                                          setSubmittingTrue();
                                          try {
                                              const { data } = await capitalOperationRecordsCreate({
                                                  user_id: values.user_id.trim(),
                                                  type: values.type,
                                                  token: values.token,
                                                  amount: values.amount,
                                                  reason: values.reason?.trim() ?? '',
                                              });
                                              if (data.code !== 200) {
                                                  message.error(data.msg || '创建失败');
                                                  return;
                                              }
                                              message.success(intl.formatMessage({ id: 'c.operationCompleted' }));
                                              onClose(true);
                                          } catch (e: any) {
                                              message.error(e?.response?.data?.msg || e?.message || '请求失败');
                                          } finally {
                                              setSubmittingFalse();
                                          }
                                      });
                                  }}
                              >
                                  提交
                              </Button>
                          </Space>
                      </div>
                  ),
        onClose: () => onClose(),
        afterOpenChange: (open) => {
            if (!open) {
                form.resetFields();
                return;
            }
            if (state.mode === 'detail') {
                const d = state.initialData;
                if (d) {
                    form.setFieldsValue({
                        user_id: d.user_id,
                        type: d.type,
                        token: d.token,
                        amount: d.amount,
                        reason: d.reason ?? '',
                    });
                } else if (state.id) {
                    capitalOperationRecordsDetail(state.id).then(({ data: res }) => {
                        if (res.code === 200 && res.data) {
                            const record = res.data;
                            form.setFieldsValue({
                                user_id: record.user_id,
                                type: record.type,
                                token: record.token,
                                amount: record.amount,
                                reason: record.reason ?? '',
                            });
                        }
                    });
                }
            } else {
                form.setFieldsValue({ token: 'USDT' });
            }
        },
    };

    const formProps: FormProps<FormValues> = {
        form,
        layout: 'vertical',
        autoComplete: 'off',
    };

    return (
        <Drawer {...drawerProps} width={480}>
            <Form {...formProps}>
                <Form.Item
                    name="user_id"
                    label="用户ID"
                    rules={[{ required: true, message: '请输入用户ID' }]}
                >
                    <Input placeholder="请输入用户ID" disabled={isDetail} />
                </Form.Item>
                <Form.Item
                    name="type"
                    label="类型"
                    rules={[{ required: true, message: '请选择类型' }]}
                >
                    <Select
                        placeholder="请选择：空投、其他加线、其他扣线等"
                        options={TYPE_OPTIONS}
                        disabled={isDetail}
                    />
                </Form.Item>
                <Form.Item
                    name="token"
                    label="币种"
                    rules={[{ required: true, message: '请选择币种' }]}
                >
                    <Select placeholder="请选择：USDT" options={TOKEN_OPTIONS} disabled={isDetail} />
                </Form.Item>
                <Form.Item
                    name="amount"
                    label="金额"
                    rules={[{ required: true, message: '请输入金额' }]}
                >
                    <InputNumber
                        placeholder="请输入金额"
                        style={{ width: '100%' }}
                        disabled={isDetail}
                        precision={2}
                        min={-1e12}
                        max={1e12}
                    />
                </Form.Item>
                <Form.Item name="reason" label="原因">
                    <Input.TextArea placeholder="请输入原因" rows={3} disabled={isDetail} />
                </Form.Item>
            </Form>
        </Drawer>
    );
};

export default ComponentDrawerForm;
